import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Divider,
} from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { CustomTheme } from "../../../../types/customTheme";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import {
  getChargebeeSubscriptionsByCustomer,
  updateLicense,
} from "../../../../services/locationApp/licenseService";
import Cookies from "js-cookie";
import ChargebeeSubscriptionList, {
  ChargebeeSubscription,
} from "./ChargebeeSubscriptionList";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    dialogPaper: {
      borderRadius: 14,
      padding: "8px 0",
    },
    title: {
      fontSize: 24,
      fontWeight: 500,
      color: theme.palette.text.primary,
      paddingBottom: 8,
    },
    contentWrapper: {
      paddingTop: 4,
    },
    infoRow: {
      display: "flex",
      alignItems: "center",
      marginBottom: 8,
      gap: 18,
    },
    infoLabel: {
      minWidth: 140,
      color: theme.palette.text.secondary,
      fontSize: 15,
      fontWeight: 500,
    },
    infoValue: {
      color: theme.palette.text.primary,
      fontSize: 15,
      fontWeight: 600,
      wordBreak: "break-word",
    },
    errorText: {
      color: theme.palette.custom.red.main,
      fontSize: 12,
      marginTop: 8,
      marginBottom: 4,
    },
    currentSubscriptionBox: {
      padding: 18,
      borderRadius: 14,
      backgroundColor: theme.palette.background.entity_background,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
    currentSubscriptionTitle: {
      fontSize: 18,
      fontWeight: 600,
      marginBottom: 10,
    },
    actionRow: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 12,
      marginTop: 18,
      flexWrap: "wrap",
    },
    dialogActions: {
      padding: "16px 24px 4px",
    },
  })
);

interface License {
  licenseCode: string;
  subscriptionId?: string;
  name: string;
  deviceType: string;
  deviceId: string;
  status: string;
  subscriptionPeriod: string;
  subscriptionAmount: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  license: License | null;
  locationId: string;
  chargebeeCustomerId: string | null;
  onUpdate: (licenseCode: string, patch: Partial<License>) => void;
}

const getToken = () => Cookies.get("idToken") || null;

const getLicenseStatusFromSubscriptionStatus = (
  subscriptionStatus?: string
) => {
  const normalizedStatus = String(subscriptionStatus || "").trim().toLowerCase();

  const lockedStatuses = ["future", "paused", "cancelled", "transferred"];
  const activeStatuses = ["in_trial", "active", "non_renewing"];

  if (lockedStatuses.includes(normalizedStatus)) return "locked";
  if (activeStatuses.includes(normalizedStatus)) return "active";

  return null;
};

const maskCustomerId = (customerId: string | null) => {
  if (!customerId) return "-";

  if (customerId.length <= 8) {
    return customerId;
  }

  return `${customerId.slice(0, 4)}...${customerId.slice(-4)}`;
};

const LicenseSubscriptionIdModal: React.FC<Props> = ({
  open,
  onClose,
  license,
  locationId,
  chargebeeCustomerId,
  onUpdate,
}) => {
  const classes = useStyles();

  const [subscriptions, setSubscriptions] = useState<ChargebeeSubscription[]>(
    []
  );
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState("");
  const [expandedSubscriptionId, setExpandedSubscriptionId] = useState("");
  const [showSubscriptionList, setShowSubscriptionList] = useState(false);

  const [error, setError] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const hasExistingSubscription = Boolean(license?.subscriptionId);

  const selectedSubscription = useMemo(() => {
    return (
      subscriptions.find(
        (subscription) => subscription.id === selectedSubscriptionId
      ) || null
    );
  }, [subscriptions, selectedSubscriptionId]);

  useEffect(() => {
    if (!open || !license) return;

    setError("");
    setSubscriptions([]);
    setExpandedSubscriptionId("");
    setSelectedSubscriptionId(license.subscriptionId || "");

    if (license.subscriptionId) {
      setShowSubscriptionList(false);
    } else {
      setShowSubscriptionList(true);
    }
  }, [open, license]);

  useEffect(() => {
    if (!open || !showSubscriptionList) return;

    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, showSubscriptionList, chargebeeCustomerId]);

  const extractSubscriptionList = (response: any): ChargebeeSubscription[] => {
    const rawList =
      response?.data?.data?.list ||
      response?.data?.list ||
      response?.data?.data?.subscriptions ||
      [];

    return rawList
      .map((item: any) => item.subscription || item)
      .filter((subscription: any) => subscription && subscription.id);
  };

  const fetchSubscriptions = async () => {
    if (!chargebeeCustomerId) {
      setError("No Chargebee customer is connected to this location.");
      setSubscriptions([]);
      return;
    }

    setIsFetching(true);
    setError("");

    try {
      const token = await getToken();

      if (!token) {
        throw new Error("Missing Firebase token");
      }

      const response = await getChargebeeSubscriptionsByCustomer(
        chargebeeCustomerId,
        token
      );

      const list = extractSubscriptionList(response);
      setSubscriptions(list);

      if (list.length === 0) {
        setError("No subscriptions found for this Chargebee customer.");
      }
    } catch (err) {
      console.error("Error fetching Chargebee subscriptions →", err);
      setError("Failed to load Chargebee subscriptions. Please try again.");
      setSubscriptions([]);
    } finally {
      setIsFetching(false);
    }
  };

  const handleSelectSubscription = (subscriptionId: string) => {
    setSelectedSubscriptionId(subscriptionId);
    setError("");
  };

  const handleToggleExpanded = (subscriptionId: string) => {
    setExpandedSubscriptionId((current) =>
      current === subscriptionId ? "" : subscriptionId
    );
  };

  const handleShowChangeList = () => {
    setError("");
    setSubscriptions([]);
    setExpandedSubscriptionId("");
    setSelectedSubscriptionId("");
    setShowSubscriptionList(true);
  };

  const handleSaveSubscription = async () => {
    if (!license) return;

    if (!selectedSubscriptionId.trim()) {
      setError("Please select a subscription.");
      return;
    }

    if (!selectedSubscription) {
      setError("Selected subscription not found.");
      return;
    }

    const mappedLicenseStatus = getLicenseStatusFromSubscriptionStatus(
      selectedSubscription.status
    );

    if (!mappedLicenseStatus) {
      setError("Invalid subscription status. Cannot update license.");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      const token = await getToken();

      if (!token) {
        throw new Error("Missing Firebase token");
      }

      await updateLicense(
        locationId,
        {
          licenseCode: license.licenseCode,
          subscriptionId: selectedSubscriptionId.trim(),
          subscriptionStatus: selectedSubscription.status,
        },
        token
      );

      onUpdate(license.licenseCode, {
        subscriptionId: selectedSubscriptionId.trim(),
        status: mappedLicenseStatus,
      });

      onClose();
    } catch (err) {
      console.error("Error updating subscription ID →", err);
      setError("Failed to update subscription. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveSubscription = async () => {
    if (!license) return;

    setIsRemoving(true);
    setError("");

    try {
      const token = await getToken();

      if (!token) {
        throw new Error("Missing Firebase token");
      }

      await updateLicense(
        locationId,
        {
          licenseCode: license.licenseCode,
          removeSubscriptionId: true,
        },
        token
      );

      onUpdate(license.licenseCode, {
        subscriptionId: undefined,
        status: "locked",
      });

      onClose();
    } catch (err) {
      console.error("Error removing subscription ID →", err);
      setError("Failed to remove subscription. Please try again.");
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.title}>
        {hasExistingSubscription
          ? "Chargebee Subscription"
          : "Link Chargebee Subscription"}
      </DialogTitle>

      <DialogContent className={classes.contentWrapper}>
        {license ? (
          <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
            <Box>
              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>License:</Typography>
                <Typography className={classes.infoValue}>
                  {license.name} ({license.licenseCode})
                </Typography>
              </Box>

              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>
                  Customer ID:
                </Typography>
                <Typography
                  className={classes.infoValue}
                  title={chargebeeCustomerId || ""}
                >
                  {maskCustomerId(chargebeeCustomerId)}
                </Typography>
              </Box>
            </Box>

            <Divider />

            {hasExistingSubscription && !showSubscriptionList ? (
              <Box className={classes.currentSubscriptionBox}>
                <Typography className={classes.currentSubscriptionTitle}>
                  Current Chargebee Subscription
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  <strong>Subscription ID:</strong> {license.subscriptionId}
                </Typography>

                <Box className={classes.actionRow}>
                  <ButtonCommon
                    variant="contained"
                    color="red"
                    onClick={handleRemoveSubscription}
                    isDisabled={isRemoving}
                    style={{ minWidth: 220, height: 40, whiteSpace: "nowrap" }}
                  >
                    {isRemoving ? "Removing..." : "Remove Subscription"}
                  </ButtonCommon>

                  <ButtonCommon
                    variant="contained"
                    color="green"
                    onClick={handleShowChangeList}
                    isDisabled={isFetching}
                    style={{ minWidth: 220, height: 40, whiteSpace: "nowrap" }}
                  >
                    Change Subscription
                  </ButtonCommon>
                </Box>
              </Box>
            ) : (
              <ChargebeeSubscriptionList
                subscriptions={subscriptions}
                selectedSubscriptionId={selectedSubscriptionId}
                expandedSubscriptionId={expandedSubscriptionId}
                isFetching={isFetching}
                onSelectSubscription={handleSelectSubscription}
                onToggleExpanded={handleToggleExpanded}
              />
            )}

            {error && (
              <Typography className={classes.errorText}>{error}</Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No license selected.
          </Typography>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <ButtonCommon
          variant="contained"
          color="yellow"
          onClick={onClose}
          isDisabled={isSaving || isRemoving}
          style={{ minWidth: 120, height: 40 }}
        >
          Cancel
        </ButtonCommon>

        {showSubscriptionList && (
          <ButtonCommon
            variant="contained"
            color="green"
            onClick={handleSaveSubscription}
            isDisabled={
              isSaving ||
              isFetching ||
              !selectedSubscriptionId.trim() ||
              !selectedSubscription
            }
            style={{ minWidth: 230, height: 40 }}
          >
            {isSaving
              ? "Saving..."
              : hasExistingSubscription
              ? "Update Subscription"
              : "Link Subscription"}
          </ButtonCommon>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LicenseSubscriptionIdModal;